import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useDropzone } from 'react-dropzone';
import PublishOutlinedIcon from '@material-ui/icons/PublishOutlined';
import { FileUpload } from '@roc/ui';
import { parseAddress } from '@roc/feature-utils';
import { useDownloadTrackRecordTemplate } from '../hooks/useDownloadTrackRecordTemplate';
import { AddressAutocompleteInput } from './addressAutocompleteInput';
import { PROPERTY_METHODS, PropertyMethod } from './trackRecordV2.config';
import { INFO_600, SUCCESS_GREEN } from './trackRecordV2.styles';

const formatElapsed = (startedAt: number) => {
  const totalSeconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Each running row owns its own 1s tick, so counters are independent of one another
// and of the store's polling; the interval is cleared as soon as the row stops running.
const ElapsedTimer = ({ startedAt, className }: { startedAt: number; className: string }) => {
  const [, forceTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => forceTick((tick) => tick + 1), 1000);
    return () => clearInterval(interval);
  }, []);
  return <Typography className={className}>{formatElapsed(startedAt)}</Typography>;
};

const formatDuration = (finishedAt: number | null, startedAt: number | null) => {
  if (finishedAt == null || startedAt == null) return null;
  const totalSeconds = Math.max(0, Math.round((finishedAt - startedAt) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `done in ${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const describeDoneResult = (resultCount: number | null) => {
  if (!resultCount) return 'No new properties found';
  const noun = resultCount === 1 ? 'property' : 'properties';
  return `${resultCount} ${noun} added under Properties and Entities`;
};

const AgentSearchListItem = ({
  classes,
  search,
  queuedSubtext,
}: {
  classes: Record<string, string>;
  search: any;
  queuedSubtext: string;
}) => {
  if (search.status === 'done') {
    return (
      <Box className={`${classes.queuedSearchRow} ${classes.queuedSearchRowDone}`}>
        <Box className={classes.queuedSearchRowLeft}>
          <CheckIcon fontSize="small" style={{ color: SUCCESS_GREEN, flexShrink: 0 }} />
          <Box>
            <Typography className={classes.queuedSearchLabel}>{search.label}</Typography>
            <Typography className={classes.queuedSearchSubtext}>{describeDoneResult(search.resultCount)}</Typography>
          </Box>
        </Box>
        <Typography className={classes.queuedSearchDoneTimer}>
          {formatDuration(search.finishedAt, search.queuedAt)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.queuedSearchRow}>
      <Box className={classes.queuedSearchRowLeft}>
        <CircularProgress size={16} thickness={4} style={{ color: INFO_600, flexShrink: 0 }} />
        <Box>
          <Typography className={classes.queuedSearchLabel}>{search.label}</Typography>
          <Typography className={classes.queuedSearchSubtext}>{queuedSubtext}</Typography>
        </Box>
      </Box>
      <ElapsedTimer startedAt={search.queuedAt} className={classes.queuedSearchTimer} />
    </Box>
  );
};

export const PropertyMethodPicker = observer(({
  classes,
  showRecommended,
  showTemplateLink,
  verificationStore,
  borrowerId,
  onNavigate,
}: {
  classes: Record<string, string>;
  showRecommended: boolean;
  showTemplateLink: boolean;
  verificationStore?: any;
  borrowerId?: number | string;
  // Jumps to the dashboard's Properties/Entities nav item once results are in.
  onNavigate?: (item: 'properties' | 'entities') => void;
}) => {
  const downloadTrackRecordTemplate = useDownloadTrackRecordTemplate();

  const [propertyMethod, setPropertyMethod] = useState<PropertyMethod>('upload');

  const [trackRecordUploads, setTrackRecordUploads] = useState<FileUpload[]>([]);

  const onDropTrackRecordFile = (acceptedFiles: File[]) => {
    setTrackRecordUploads(
      acceptedFiles.map((file) => ({
        name: file.name,
        file,
        type: file.name.split('.').pop(),
      })) as FileUpload[]
    );
  };
  const {
    getRootProps: getTrackRecordRootProps,
    getInputProps: getTrackRecordInputProps,
    isDragActive: isTrackRecordDragActive,
  } = useDropzone({ onDrop: onDropTrackRecordFile, multiple: false, maxFiles: 1 });

  useEffect(() => {
    if (!verificationStore || trackRecordUploads.length === 0) return;

    const run = async () => {
      try {
        await verificationStore.setTemplateUploads(trackRecordUploads);

        const id = borrowerId ?? verificationStore.borrowerInfo?.borrowerId ?? verificationStore.borrowerId;
        if (!id) {
          verificationStore.globalStore?.notificationStore?.showErrorNotification?.({
            message: 'Borrower information is still loading. Please try again in a moment.',
          });
          return;
        }

        // Agent flow only: stores the excel on the borrower's track record task, invokes the agent and tracks it in the top banner.
        verificationStore.queueTrackRecordSearch(trackRecordUploads[0].name, 'UPLOAD', { trackRecordExcel: trackRecordUploads[0].name });
      } catch (uploadError) {
        console.error('Track record template upload failed:', uploadError);
        verificationStore.globalStore?.notificationStore?.showErrorNotification?.({
          message: 'Error occurred while uploading the track record template',
        });
      }
    };

    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackRecordUploads, verificationStore, borrowerId]);

  const [entitySearchValue, setEntitySearchValue] = useState('');

  // Both lookups hand off to async AI processing, tracked in the store as
  // pendingAgentSearches (auto-polled — see queueTrackRecordSearch). Each running
  // row counts up from its own queuedAt (see ElapsedTimer).
  const entitySearches = (verificationStore?.pendingAgentSearches ?? []).filter(
    (search: any) => search.flow === 'ENTITY_LOOKUP'
  );
  const propertySearches = (verificationStore?.pendingAgentSearches ?? []).filter(
    (search: any) => search.flow === 'PROPERTY_LOOKUP'
  );

  const searchEntityTrackRecords = () => {
    const name = (entitySearchValue || '').trim();
    if (!name) return;
    // queueTrackRecordSearch no-ops silently without a resolved borrowerId.
    const id = borrowerId ?? verificationStore?.borrowerInfo?.borrowerId ?? verificationStore?.borrowerId;
    if (!id || !verificationStore?.queueTrackRecordSearch) {
      verificationStore?.globalStore?.notificationStore?.showErrorNotification?.({
        message: 'Borrower information is still loading. Please try again in a moment.',
      });
      return;
    }
    verificationStore.queueTrackRecordSearch(name, 'ENTITY_LOOKUP', { entityNames: [name] });
    setEntitySearchValue('');
  };

  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertyAptUnit, setPropertyAptUnit] = useState('');
  const [propertyCity, setPropertyCity] = useState('');
  const [propertyState, setPropertyState] = useState('');
  const [propertyZip, setPropertyZip] = useState('');

  const handlePropertyAddressChange = (event: any) => {
    const { value, fullInfo } = event?.target || {};
    setPropertyAddress(value ?? '');
    if (fullInfo?.address_components) {
      const parsed = parseAddress(value, fullInfo.address_components, fullInfo.geometry);
      if (parsed.city) setPropertyCity(parsed.city);
      if (parsed.state) setPropertyState(parsed.state);
      if (parsed.zip) setPropertyZip(parsed.zip);
    }
  };

  const searchPropertyLookup = () => {
    const id = borrowerId ?? verificationStore?.borrowerInfo?.borrowerId ?? verificationStore?.borrowerId;
    if (!id || !verificationStore?.queueTrackRecordSearch) {
      verificationStore?.globalStore?.notificationStore?.showErrorNotification?.({
        message: 'Borrower information is still loading. Please try again in a moment.',
      });
      return;
    }
    const label = [propertyAddress, propertyCity, propertyState].filter(Boolean).join(', ') || 'this property';
    // Same pattern as fireAgentForProperty in borrowerTrackRecordVerification.tsx.
    verificationStore.queueTrackRecordSearch(label, 'PROPERTY_LOOKUP', {
      customTransactions: [{
        street_address: propertyAddress,
        street_address_line_2: propertyAptUnit,
        city: propertyCity,
        state: propertyState,
        zip_code: propertyZip,
      }],
    });
    setPropertyAddress('');
    setPropertyAptUnit('');
    setPropertyCity('');
    setPropertyState('');
    setPropertyZip('');
  };

  const uploadSubtext = showTemplateLink ? (
    <>
      XLSX or PDF{' '}
      <span
        className={classes.dropZoneLink}
        onClick={(event) => {
          // Inside the dropzone's own click-to-upload area — stop the click
          // from also opening the file picker.
          event.stopPropagation();
          downloadTrackRecordTemplate();
        }}
      >
        Download Capital Provider Template
      </span>
    </>
  ) : (
    'XLSX or PDF · up to 25 MB'
  );

  return (
    <>
      <Box className={classes.methodOptionsRow}>
        {PROPERTY_METHODS.map(({ key, icon: Icon, title, subtitle, recommended }) => (
          <Box
            key={key}
            className={`${classes.methodCard} ${propertyMethod === key ? classes.methodCardSelected : ''}`}
            onClick={() => setPropertyMethod(key)}
          >
            {showRecommended && recommended && <span className={classes.recommendedChip}>Recommended</span>}
            <Icon className={classes.methodIcon} />
            <Box>
              <Typography className={classes.methodTitle}>{title}</Typography>
              <Typography className={classes.methodSubtitle}>{subtitle}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {propertyMethod === 'upload' && (
        <Box className={classes.dropZone} {...getTrackRecordRootProps()}>
          <input {...getTrackRecordInputProps()} />
          <PublishOutlinedIcon className={classes.dropZoneIcon} />
          {isTrackRecordDragActive ? (
            <Typography className={classes.dropZoneText}>Drop the file here...</Typography>
          ) : trackRecordUploads[0] ? (
            <Typography className={classes.dropZoneText}>{trackRecordUploads[0].name}</Typography>
          ) : (
            <Typography className={classes.dropZoneText}>
              Drag &amp; drop your track record here or{' '}
              <span className={classes.dropZoneLink}>click here to upload</span>
            </Typography>
          )}
          <Typography className={classes.dropZoneSubtext}>{uploadSubtext}</Typography>
        </Box>
      )}
      {propertyMethod === 'entity' && (
        <Box>
          <Typography className={classes.searchHelperText}>
            Search an LLC you've owned property under and we'll pull its records.
          </Typography>
          <Box className={classes.searchRow}>
            <input
              className={classes.searchInput}
              placeholder="Search entity name..."
              value={entitySearchValue}
              onChange={(event) => setEntitySearchValue(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && searchEntityTrackRecords()}
            />
            <button type="button" className={classes.searchButton} onClick={searchEntityTrackRecords}>
              Search
            </button>
          </Box>
          {entitySearches.map((search: any) => (
            <AgentSearchListItem
              key={search.id}
              classes={classes}
              search={search}
              queuedSubtext="Queued — searching registries, deed filings and county records"
            />
          ))}
        </Box>
      )}
      {propertyMethod === 'property' && (
        <Box>
          <Typography className={classes.searchHelperText}>
            Enter one address at a time and we'll pull the recorded deed history.
          </Typography>
          <Box className={`${classes.searchRow} ${classes.searchRowSpaced}`}>
            <AddressAutocompleteInput
              className={`${classes.searchInput} ${classes.searchInputAddress}`}
              placeholder="Property address *"
              value={propertyAddress}
              onChange={handlePropertyAddressChange}
            />
            <input
              className={`${classes.searchInput} ${classes.searchInputApt}`}
              placeholder="Apt / Unit #"
              value={propertyAptUnit}
              onChange={(event) => setPropertyAptUnit(event.target.value)}
            />
          </Box>
          <Box className={classes.searchRow}>
            <input
              className={`${classes.searchInput} ${classes.searchInputCity}`}
              placeholder="City"
              value={propertyCity}
              onChange={(event) => setPropertyCity(event.target.value)}
            />
            <input
              className={`${classes.searchInput} ${classes.searchInputState}`}
              placeholder="State *"
              value={propertyState}
              onChange={(event) => setPropertyState(event.target.value)}
            />
            <input
              className={`${classes.searchInput} ${classes.searchInputZip}`}
              placeholder="Zip"
              value={propertyZip}
              onChange={(event) => setPropertyZip(event.target.value)}
            />
            <button type="button" className={classes.searchButton} onClick={searchPropertyLookup}>
              Search
            </button>
          </Box>
          {propertySearches.map((search: any) => (
            <AgentSearchListItem
              key={search.id}
              classes={classes}
              search={search}
              queuedSubtext="Queued — searching deed filings and county records"
            />
          ))}
        </Box>
      )}
    </>
  );
});
